// Uppercase ISO 639-1 Language Codes
// el -> greek, tr -> turskish
// https://en.wikipedia.org/wiki/List_of_ISO_639_language_codes -> use Set 1
export type Language =
    | "DE"
    | "EN"
    | "PL"
    | "ES"
    | "FR"
    | "CZ"
    | "IT"
    | "PT"
    | "PTBR"
    | "SV"
    | "RO"
    | "NL"
    | "TR"
    | "EL"
    | "ET"
    | "LV"
    | "LT";

export type Country =
    | "DE"
    | "AT"
    | "CH"
    | "GB"
    | "CZ"
    | "PL"
    | "IT"
    | "FR"
    | "ES"
    | "PT"
    | "BR"
    | "SE"
    | "RO"
    | "NL"
    | "BE"
    | "TR"
    | "GR"
    | "MX"
    | "EE"
    | "LV"
    | "LT"
    | "CA"
    | "TN"
    | "MA";

export type Locale =
    | "DE-DE" //
    | "DE-AT"
    | "DE-CH"
    | "EN-GB"
    | "PL-PL"
    | "ES-ES"
    | "FR-FR"
    | "CZ-CZ"
    | "IT-IT"
    | "PT-PT"
    | "PT-BR"
    | "SV-SE"
    | "RO-RO"
    | "EL-GR"
    | "TR-TR"
    | "NL-NL"
    | "FR-BE"
    | "NL-BE"
    | "ES-MX"
    | "ET-EE"
    | "LV-LV"
    | "LT-LT"
    | "FR-CA"
    | "EN-CA"
    | "FR-TN"
    | "FR-MA";

export const PATH_FOR_LOCALE = {
    "DE-DE": "/de-de",
    "DE-AT": "/de-at",
    "DE-CH": "/de-ch",
    "EN-GB": "/en-gb",
    "PL-PL": "/pl-pl",
    "ES-ES": "/es-es",
    "FR-FR": "/fr-fr",
    "IT-IT": "/it-it",
    "CZ-CZ": "/cz-cz",
    "PT-PT": "/pt-pt",
    "PT-BR": "/pt-br",
    "SV-SE": "/sv-se",
    "RO-RO": "/ro-ro",
    "EL-GR": "/el-gr",
    "FR-BE": "/fr-be",
    "NL-BE": "/nl-be",
    "NL-NL": "/nl-nl",
    "TR-TR": "/tr-tr",
    "ES-MX": "/es-mx",
    "ET-EE": "/et-ee",
    "LV-LV": "/lv-lv",
    "LT-LT": "/lt-lt",
    "FR-CA": "/fr-ca",
    "EN-CA": "/en-ca",
    "FR-TN": "/fr-tn",
    "FR-MA": "/fr-ma",
} satisfies {
    [K in Locale]: `/${Lowercase<K>}`;
};

export const LANGUAGE_FOR_LOCALE = {
    "DE-DE": "DE",
    "DE-AT": "DE",
    "DE-CH": "DE",
    "EN-GB": "EN",
    "PL-PL": "PL",
    "ES-ES": "ES",
    "FR-FR": "FR",
    "IT-IT": "IT",
    "CZ-CZ": "CZ",
    "PT-PT": "PT",
    "PT-BR": "PTBR",
    "SV-SE": "SV",
    "RO-RO": "RO",
    "EL-GR": "EL",
    "FR-BE": "FR",
    "NL-BE": "NL",
    "NL-NL": "NL",
    "TR-TR": "TR",
    "ES-MX": "ES",
    "ET-EE": "ET",
    "LV-LV": "LV",
    "LT-LT": "LT",
    "FR-CA": "FR",
    "EN-CA": "EN",
    "FR-TN": "FR",
    "FR-MA": "FR",
} satisfies Record<Locale, Language>;

export const COUNTRY_FOR_LOCALE = {
    "DE-DE": "DE",
    "DE-AT": "AT",
    "DE-CH": "CH",
    "EN-GB": "GB",
    "PL-PL": "PL",
    "ES-ES": "ES",
    "FR-FR": "FR",
    "IT-IT": "IT",
    "CZ-CZ": "CZ",
    "PT-PT": "PT",
    "PT-BR": "BR",
    "SV-SE": "SE",
    "RO-RO": "RO",
    "EL-GR": "GR",
    "FR-BE": "BE",
    "NL-BE": "BE",
    "NL-NL": "NL",
    "TR-TR": "TR",
    "ES-MX": "MX",
    "ET-EE": "EE",
    "LV-LV": "LV",
    "LT-LT": "LT",
    "FR-CA": "CA",
    "EN-CA": "CA",
    "FR-TN": "TN",
    "FR-MA": "MA",
} satisfies Record<Locale, Country>;

export const DEFAULT_LOCALE_FOR_COUNTRY = {
    DE: "de-de",
    AT: "de-at",
    CH: "de-ch",
    GB: "en-gb",
    PL: "pl-pl",
    ES: "es-es",
    FR: "fr-fr",
    IT: "it-it",
    CZ: "cz-cz",
    PT: "pt-pt",
    BR: "pt-br",
    SE: "sv-se",
    RO: "ro-ro",
    BE: "nl-be",
    GR: "el-gr",
    NL: "nl-nl",
    TR: "tr-tr",
    MX: "es-mx",
    EE: "et-ee",
    LV: "lv-lv",
    LT: "lt-lt",
    CA: "en-ca",
    TN: "fr-tn",
    MA: "fr-ma",
} satisfies Record<Country, Lowercase<Locale>>;

export const ALL_LOCALES = Object.keys(COUNTRY_FOR_LOCALE).map((m) => m.toLowerCase()) as Lowercase<Locale>[];

type Split<S extends string, D extends string> = string extends S
    ? string[]
    : S extends ""
      ? []
      : S extends `${infer T}${D}${infer U}`
        ? [T, ...Split<U, D>]
        : [S];

export type CountryFromLocale<L extends Locale> = Split<L, "-">[1];
export type LanguageFromLocale<L extends Locale> = Split<L, "-">[1];
